import { useEffect, useState, type FormEvent } from "react";
import { Search, X } from "lucide-react";
import { cn } from "@onemileauto/ui";

interface SearchBarProps {
  value: string;
  onChange: (q: string) => void;
  placeholder?: string;
  className?: string;
}

/**
 * Search input that commits to `onChange` (which drives the `q` URL search
 * param and navigation to results) only on explicit submit — pressing
 * Enter/the keyboard's search key, or tapping the magnifying-glass icon —
 * never as a side effect of typing. Chosen deliberately over live-as-you-type
 * search so it can't feel like it "interrupted" reading a message; the icon
 * doubles as a real submit button (not just decorative) so there's an
 * obvious, big tap target rather than relying on a virtual keyboard's small
 * search/go key.
 *
 * Styled to read as a prominent, "global" search affordance (persistent
 * pill on `bg-white`, its own shadow) rather than a small filter box
 * scoped inside the list pane — it's mounted once in the main pane's top
 * bar and stays visible in both list and detail mode.
 *
 * The icon/clear-button are positioned against a bare `relative` wrapper
 * with no vertical padding of its own, so `top-1/2` centers them exactly
 * on the input's box — callers control outer spacing via `className`
 * instead of this component adding its own padding into the mix.
 */
export function SearchBar({ value, onChange, placeholder = "Search mail", className }: SearchBarProps) {
  const [draft, setDraft] = useState(value);

  // Keep the local draft in sync if the URL's q param changes from outside
  // (e.g. browser back/forward, or clearing search elsewhere).
  useEffect(() => {
    setDraft(value);
  }, [value]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    onChange(draft);
  }

  function handleClear() {
    setDraft("");
    onChange("");
  }

  return (
    <form onSubmit={handleSubmit} className={cn("relative", className)}>
      <button
        type="submit"
        aria-label="Search"
        className="absolute left-1.5 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-muted hover:bg-parchment-dim hover:text-charcoal"
      >
        <Search size={19} />
      </button>
      <input
        type="search"
        inputMode="search"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={placeholder}
        aria-label="Search mail"
        className="h-12 w-full rounded-full border border-border bg-white pl-11 pr-11 text-[15px] text-charcoal shadow-sm placeholder:text-muted focus:border-rust focus:outline-none focus:ring-2 focus:ring-rust/30"
      />
      {draft && (
        <button
          type="button"
          onClick={handleClear}
          aria-label="Clear search"
          className="absolute right-1.5 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-muted hover:bg-parchment-dim hover:text-charcoal"
        >
          <X size={16} />
        </button>
      )}
    </form>
  );
}
